import {
  CalendarMonthOutlineIcon,
  CityIcon,
  CodeOutlineIcon,
  CopyOutlineIcon,
  EarthIcon,
  InfinityOutlineIcon,
  LockIcon,
} from '@carboncopyco/icons';
import type { ShareLink } from '@carboncopyco/types';
import { ShareSecurityLevel } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Modal,
  Separator,
  TextBase,
  TextCaption,
  TooltipBubble,
} from '@carboncopyco/ui';
import { MMMDoYYYYUnix, createEmbeddedIframeCode } from '@carboncopyco/utils';
import { type ReactNode, useMemo } from 'react';
import { useCopy } from '../utils';
import { FilterEmailsLabel } from './FilterEmailsLabel';
import { useShowtimeUrl } from '@carboncopyco/components';

interface CopySharePresentationLinkModalProps {
  open: boolean;
  onClose: () => void;
  goToShares?: () => void;
  shareLink: ShareLink;
  title?: string;
  tour?: boolean;
  subText?: ReactNode;
  containerClass?: string;
}

export const CopyShareLinkModal = ({
  open,
  onClose,
  title = 'Share was successfully created!',
  shareLink,
  tour,
  goToShares,
  subText,
  containerClass = 'w-[656px]',
}: CopySharePresentationLinkModalProps) => {
  const { copyText } = useCopy();
  const { showtimeShareUrl } = useShowtimeUrl();

  const shareLinkOptions = 'options' in shareLink ? shareLink?.options : null;
  const newLinkSecurityLevel = shareLinkOptions?.security?.level;

  const infoLabelBySecurityLevel = useMemo<
    Record<ShareSecurityLevel, ReactNode>
  >(
    () => ({
      [ShareSecurityLevel.internal]: (
        <>
          <InfinityOutlineIcon
            color={ColorHex.natural300}
            size={16}
          />
          <TextCaption label="Accessible only to internal users" />
        </>
      ),
      [ShareSecurityLevel.restricted]: (
        <>
          <LockIcon
            color={ColorHex.natural300}
            size={16}
          />
          <TextCaption label="Only visitors with a pre-defined email address can view" />
        </>
      ),
      [ShareSecurityLevel.emailDomain]: (
        <>
          <CityIcon
            color={ColorHex.natural300}
            size={16}
          />
          <TextCaption label="Accessible to visitors in the specified domain" />
        </>
      ),
      [ShareSecurityLevel.anyone]: (
        <>
          <EarthIcon
            color={ColorHex.natural300}
            size={16}
          />
          <TextCaption label="Anyone with the link can view" />
          {shareLinkOptions?.security.filterEmailDomains && (
            <div className="flex gap-1.5">
              <div>
                <Separator />
              </div>
              <FilterEmailsLabel caption />
            </div>
          )}
        </>
      ),
    }),
    [shareLinkOptions?.security]
  );
  const shareUrl = showtimeShareUrl(shareLink.token);
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      containerClass={containerClass}
    >
      <div>
        <div className="flex flex-col mt-2 mb-8">
          {subText ? (
            subText
          ) : (
            <TextBase
              label="Copy the link below and share your sandbox"
              color="text-ds-natural-400"
            />
          )}
        </div>

        <div className="flex w-full items-center justify-between mb-2.5">
          <TooltipBubble
            placement="top"
            title={shareUrl}
          >
            <div className="flex items-center w-ds-500 h-8 bg-ds-natural-50 rounded-md overflow-hidden">
              <div className="p-2 truncate text-ds-natural-600">
                <TextBase
                  label={shareUrl}
                  color="text-ds-natural-600"
                  ellipsis
                />
              </div>
            </div>
          </TooltipBubble>

          <Button
            aria-label="Copy link"
            link
            leftIcon={<CopyOutlineIcon />}
            label="Copy link"
            onClick={() =>
              copyText(shareUrl, 'Link was copied to your clipboard')
            }
          />
        </div>
        {tour && shareLink && (
          <div className="flex w-full items-center justify-between mb-2.5">
            <div className="flex items-center w-ds-500 bg-ds-natural-50 rounded-md overflow-x-auto">
              <div className="p-2 text-ds-natural-600">
                <TextBase
                  label={createEmbeddedIframeCode(shareUrl, 'Embedded tour')}
                  color="text-ds-natural-600"
                />
              </div>
            </div>
            <Button
              aria-label="Copy link"
              link
              leftIcon={<CodeOutlineIcon />}
              label="Copy link"
              onClick={() =>
                copyText(
                  createEmbeddedIframeCode(shareUrl, 'Embedded tour'),
                  'Link was copied to your clipboard'
                )
              }
            />
          </div>
        )}
        <div className="flex items-center pb-11">
          <div className="flex justify-between items-center">
            <div
              className="flex pl-2.5 items-center gap-1.5"
              aria-label="share types menu"
            >
              {infoLabelBySecurityLevel[newLinkSecurityLevel]}
            </div>
          </div>
          {shareLinkOptions?.expiresAt && (
            <div className="flex items-center mr-1.5">
              <div className="h-5 px-3">
                <Separator />
              </div>
              <div className="mr-1.5">
                <CalendarMonthOutlineIcon
                  size={16}
                  color={ColorHex.natural300}
                />
              </div>
              <TextCaption
                color="text-ds-natural-400"
                label={`Expiration: ${MMMDoYYYYUnix(shareLinkOptions?.expiresAt)}`}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-6 p-2">
          {goToShares && (
            <Button
              link
              blue
              label="View shares"
              onClick={() => {
                goToShares();
                onClose();
              }}
            />
          )}
          <Button
            label="Got it"
            onClick={onClose}
          />
        </div>
      </div>
    </Modal>
  );
};
